import { useState, useEffect, useRef } from 'react';
import { FaComments, FaReply, FaTrash, FaEnvelope, FaEnvelopeOpen, FaClock, FaPaperPlane } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchHostMessages, markMessageAsRead, deleteMessageApi, sendHostToGuestMessage, fetchConversation } from '../services/messagingService';
import './HostMessages.css';

const HostMessages = ({ hostId, hostName }) => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [conversationThread, setConversationThread] = useState([]);
  const chatBodyRef = useRef(null);

  useEffect(() => {
    loadMessages();
    
    // Listen for storage events to update messages in real-time
    const handleStorageChange = () => {
      loadMessages();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [hostId]);

  useEffect(() => {
    filterMessages();
  }, [filter, messages]);

  useEffect(() => {
    if (chatBodyRef.current && conversationThread.length > 0) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [conversationThread]);

  const loadMessages = async () => {
    const allMessages = await fetchHostMessages();
    setMessages(allMessages);
    setFilteredMessages(allMessages);
  };

  const filterMessages = () => {
    let filtered = messages;
    
    if (filter === 'unread') {
      filtered = messages.filter(m => !m.read);
    } else if (filter === 'read') {
      filtered = messages.filter(m => m.read);
    }
    
    setFilteredMessages(filtered);
  };

  const handleMarkAsRead = (messageId) => {
    markMessageAsRead(messageId, hostId, 'host');
    loadMessages();
  };

  const handleDelete = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      const ok = await deleteMessageApi(messageId);
      if (ok) loadMessages();
    }
  };

  const loadConversationThread = async (message) => {
    if (!message) return;
    const thread = await fetchConversation(message.bookingId);
    setConversationThread(thread);
  };

  const handleReply = async (message) => {
    setSelectedMessage(message);
    setShowReplyModal(true);
    setReplyText('');
    loadConversationThread(message);
    
    // Mark as read when replying
    if (!message.read) {
      await handleMarkAsRead(message.id);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.warning('Please enter a reply message');
      return;
    }

    setSendingReply(true);
    try {
      const result = await sendHostToGuestMessage({
        hostId: hostId,
        guestId: selectedMessage.fromId,
        bookingId: selectedMessage.bookingId,
        propertyName: selectedMessage.propertyName,
        message: replyText,
        hostName: hostName || 'Host'
      });

      if (result.success) {
        loadConversationThread(selectedMessage);
        setReplyText('');
      } else {
        toast.error('Failed to send reply. Please try again.');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="host-messages-container">
      <div className="messages-header">
        <div className="header-title">
          <FaComments className="header-icon" />
          <div>
            <h2>Guest Messages</h2>
            <p>Messages from your guests</p>
          </div>
        </div>
        {unreadCount > 0 && (
          <div className="unread-badge">{unreadCount} unread</div>
        )}
      </div>

      <div className="messages-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({messages.length})
        </button>
        <button
          className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          <FaEnvelope /> Unread ({unreadCount})
        </button>
        <button
          className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
          onClick={() => setFilter('read')}
        >
          <FaEnvelopeOpen /> Read ({messages.length - unreadCount})
        </button>
      </div>

      {filteredMessages.length === 0 ? (
        <div className="no-messages">
          <FaComments className="no-messages-icon" />
          <h3>No messages</h3>
          <p>
            {filter === 'unread'
              ? "You're all caught up! No unread messages."
              : filter === 'read'
              ? "No read messages yet."
              : "You don't have any messages yet."}
          </p>
        </div>
      ) : (
        <div className="messages-list">
          {filteredMessages.map((message) => (
            <div
              key={message.id}
              className={`message-card ${!message.read ? 'unread' : ''}`}
            >
              <div className="message-header">
                <div className="message-from">
                  <strong>{message.fromName}</strong>
                  <span className="message-time">
                    <FaClock /> {formatTime(message.timestamp)}
                  </span>
                </div>
                {!message.read && <div className="unread-dot"></div>}
              </div>

              <div className="message-meta">
                <span><strong>Property:</strong> {message.propertyName}</span>
                <span><strong>Booking:</strong> {message.bookingId}</span>
              </div>

              <div className="message-content">
                <p>{message.message}</p>
              </div>

              <div className="message-actions">
                <button
                  className="btn-reply"
                  onClick={() => handleReply(message)}
                >
                  <FaReply /> Reply
                </button>
                {!message.read && (
                  <button
                    className="btn-mark-read"
                    onClick={() => handleMarkAsRead(message.id)}
                  >
                    <FaEnvelopeOpen /> Mark as Read
                  </button>
                )}
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(message.id)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Modal - Chat style */}
      {showReplyModal && selectedMessage && (
        <div className="modal-overlay" onClick={() => setShowReplyModal(false)}>
          <div className="reply-modal reply-modal--chat" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header chat-modal-header">
              <div className="chat-modal-header-content">
                <h3>Chat with {selectedMessage.fromName}</h3>
                <div className="chat-meta">
                  <span>{selectedMessage.propertyName}</span>
                  <span>Booking: {selectedMessage.bookingId}</span>
                </div>
              </div>
              <button className="btn-close" onClick={() => setShowReplyModal(false)}>×</button>
            </div>
            <div className="chat-body" ref={chatBodyRef}>
              {conversationThread.length === 0 ? (
                <div className="chat-empty">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                conversationThread.map((msg) => (
                  <div
                    key={msg.id}
                    className={`chat-bubble chat-bubble--${msg.from}`}
                  >
                    <p className="chat-bubble-text">{msg.message}</p>
                    <span className="chat-bubble-time">{formatTime(msg.timestamp)}</span>
                  </div>
                ))
              )}
            </div>
            <div className="chat-input-bar">
              <textarea
                className="chat-input"
                placeholder="Type your reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
                disabled={sendingReply}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
              />
              <button
                className="btn-send-chat"
                onClick={handleSendReply}
                disabled={sendingReply || !replyText.trim()}
                title="Send"
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostMessages;


